import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { Pie } from 'react-chartjs-2';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import { useRealtimeUnits } from 'Core/hooks';
import {
  LineController,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement
} from 'chart.js';
import { getUnitsNaturalOrder } from "Core/data/Helpers";

import ChartJS from 'chart.js/auto';

ChartJS.register(
  LineController,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement
);

const ChartPie = ({ data }) => {
  
  const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
  const unitDashboard = useMemo(() => {
    const orderedUnits = getUnitsNaturalOrder(units);
    return orderedUnits.map(unit => ({
      id: unit?.id,
      label: unit?.label
    }));
  }, [units]);

  const [devices, setDevices] = useState([]);
  const [dataPie, setDataPie] = useState(data);

  useEffect(() => {
    setDataPie(data);
  }, [data]);

  const option = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: data.unit,
        position: "left"
      },
      tooltip: {
        enabled: true,
        usePointStyle: true,
        callbacks: {
          title: (datas) => datas[0].dataset.label,
          label: (datas) => {
            const percentage = data.percentage ? '%' : '';
            const value = datas.raw % 1 !== 0 ? Number(datas.raw).toFixed(2) : datas.raw;
            return ` ${datas.label} ${value} ${percentage}`;
          }
        },
      },
    },
  }), [data]);

  useEffect(() => {
    if (data?.datasets?.length > 0 && data?.show_labels) {
      const response = [];
      let cont = 0;
      data.datasets.forEach((dataset) => {
        for (const property in dataset.devices) {
          const unit = unitDashboard.find((unit) => unit.id === property);
          if (!response.some((res) => res.id === property)) {
            response.push({
              id: property,
              name: unit?.label,
              active: true,
              color: data.backgrounds[cont]
            });
            cont = cont > 21 ? 0 : cont + 1;
          }
        }
      });
      setDevices(response);
    }
  }, [data, unitDashboard]);

  const changeDevices = useCallback((device) => {
    const updatedDatasets = dataPie.datasets.map((dataset) => {
      const datasetResponse = dataset.devices[device.id]
        ? dataset.data.map((datas, key) => device.active ? datas - dataset.devices[device.id][key] : datas + dataset.devices[device.id][key])
        : dataset.data;
      return { ...dataset, data: datasetResponse };
    });

    setDataPie((prevDataPie) => ({
      ...prevDataPie,
      datasets: updatedDatasets
    }));

    setDevices((prevDevices) => prevDevices.map((g) => g.id !== device.id ? g : { ...g, active: !g.active }));
  }, [dataPie]);

  return (
    <>
      <div className="title_chart">
        <Typography className="content-text" variant="h6">
          {data.title}
        </Typography>
        <Typography className="content-text">
          {data.subTitle}
        </Typography>
      </div>

      <div className={data?.show_labels ? 'body_chart_pie' : 'body_chart'}>
        {dataPie?.data && <Pie data={dataPie} options={option} aria-label={data.title || data.unit} />}
      </div>

      {data?.show_labels && (
        <div className="devices_content">
          {devices.map((item, index) => (
            <div
              className={`chart_units ${item.active ? '' : 'devices_active'}`}
              onClick={() => changeDevices(item)}
              key={index}
            >
              <div className="chart_units_color" style={{ background: item.color }}></div>
              <div>{item.name}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default memo(ChartPie);
